import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, ShieldCheck, Loader2, CheckCircle2, XCircle, CalendarClock, HandCoins } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (v) => `$${Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const LiveCorrectionTool = ({ token }) => {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [selected, setSelected] = useState(null);

  const [target, setTarget] = useState("0");
  const [balPreview, setBalPreview] = useState(null);
  const [balBusy, setBalBusy] = useState(false);
  const [balPhrase, setBalPhrase] = useState("");

  const [candidates, setCandidates] = useState(null);
  const [rescheduleSel, setRescheduleSel] = useState(null);
  const [newDue, setNewDue] = useState("");
  const [schedPreview, setSchedPreview] = useState(null);
  const [schedBusy, setSchedBusy] = useState(false);
  const [schedPhrase, setSchedPhrase] = useState("");

  const search = async () => {
    if (query.trim().length < 2) return;
    setSearching(true);
    setResults(null);
    try {
      const { data } = await axios.get(`${API}/finance/live-correction/search`, { ...auth, params: { name: query } });
      setResults(data.results || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Search failed.");
    } finally {
      setSearching(false);
    }
  };

  const pickBooking = (b) => {
    setSelected(b);
    setBalPreview(null);
    setCandidates(null);
    setRescheduleSel(null);
    setSchedPreview(null);
    setTarget(String(b.balance || 0));
  };

  const previewBalance = async () => {
    setBalBusy(true);
    setBalPreview(null);
    try {
      const { data } = await axios.get(`${API}/finance/live-correction/balance-reconcile/preview`, {
        ...auth, params: { booking_id: selected.booking_id, target_balance: Number(target) || 0 },
      });
      setBalPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed.");
    } finally {
      setBalBusy(false);
    }
  };

  const applyBalance = async () => {
    setBalBusy(true);
    try {
      const { data } = await axios.post(`${API}/finance/live-correction/balance-reconcile/apply`, {
        booking_id: selected.booking_id, target_balance: Number(target) || 0, confirm_phrase: balPhrase,
      }, auth);
      toast.success(data.already_applied ? "Already correct — no changes made." : `Balance corrected to ${money(data.balance)}.`);
      setBalPreview(null);
      setBalPhrase("");
      search();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Apply failed.");
    } finally {
      setBalBusy(false);
    }
  };

  const loadCandidates = async () => {
    try {
      const { data } = await axios.get(`${API}/finance/live-correction/reschedule/candidates`, {
        ...auth, params: { booking_id: selected.booking_id },
      });
      setCandidates(data.candidates || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load installments.");
    }
  };

  const previewReschedule = async () => {
    if (!rescheduleSel || !newDue) return;
    setSchedBusy(true);
    setSchedPreview(null);
    try {
      const { data } = await axios.get(`${API}/finance/live-correction/reschedule/preview`, {
        ...auth, params: { booking_id: selected.booking_id, plan_id: rescheduleSel.plan_id,
          installment_n: rescheduleSel.installment_n, new_due_date: newDue },
      });
      setSchedPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed.");
    } finally {
      setSchedBusy(false);
    }
  };

  const applyReschedule = async () => {
    setSchedBusy(true);
    try {
      const { data } = await axios.post(`${API}/finance/live-correction/reschedule/apply`, {
        booking_id: selected.booking_id, plan_id: rescheduleSel.plan_id, installment_n: rescheduleSel.installment_n,
        new_due_date: newDue, confirm_phrase: schedPhrase,
      }, auth);
      toast.success(data.already_applied ? "Already correct — no changes made." : `Due date moved to ${data.new_due_date}.`);
      setSchedPreview(null);
      setSchedPhrase("");
      loadCandidates();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Apply failed.");
    } finally {
      setSchedBusy(false);
    }
  };

  return (
    <div data-testid="live-correction-tool" className="rounded-2xl border border-white/10 bg-[#0C0C0C] p-5 space-y-4">
      <div>
        <h3 className="font-display font-bold text-white flex items-center gap-2"><ShieldCheck size={18} className="text-[#F2B705]" /> Live Balance / Schedule Correction</h3>
        <p className="text-white/45 text-sm mt-0.5">Owner-authorized, one-off corrections. Search a renter, preview the exact before/after, then type the confirm phrase to apply. amount_paid is never fabricated; nothing is deleted.</p>
      </div>

      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <input data-testid="live-correction-search-input" value={query} onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && search()} placeholder="Search renter by name (e.g. Andrea Arnold)…"
            className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg pl-9 pr-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#F2B705]/50" />
        </div>
        <button data-testid="live-correction-search-btn" onClick={search} disabled={searching}
          className="bg-[#F2B705] text-black font-bold px-5 rounded-lg hover:bg-white transition-colors disabled:opacity-60">
          {searching ? <Loader2 className="animate-spin" size={16} /> : "Search"}
        </button>
      </div>

      {results && (
        <div className="space-y-1.5">
          {results.length === 0 && <div className="text-white/40 text-sm">No matching non-cancelled bookings found.</div>}
          {results.map((b) => (
            <button key={b.booking_id} data-testid={`live-correction-result-${b.booking_id}`} onClick={() => pickBooking(b)}
              className={`w-full text-left rounded-lg border px-3.5 py-2.5 transition-colors ${selected?.booking_id === b.booking_id ? "border-[#F2B705]/50 bg-[#F2B705]/[0.06]" : "border-white/10 hover:border-white/25"}`}>
              <div className="flex items-center justify-between">
                <span className="text-white/90 font-semibold text-sm">{b.name} <span className="text-white/35 text-xs font-normal">· {b.vehicle}</span></span>
                <span className="text-white/60 text-xs">{b.lifecycle_status} / {b.status}</span>
              </div>
              <div className="text-white/40 text-xs mt-1">Total {money(b.total_price)} · Discount {money(b.discount)} · Paid {money(b.amount_paid)} · Balance <span className="text-white/70 font-semibold">{money(b.balance)}</span>{b.credit > 0 ? ` · Credit ${money(b.credit)}` : ""}</div>
            </button>
          ))}
        </div>
      )}

      {selected && (
        <div className="border-t border-white/10 pt-4 space-y-5">
          <div className="text-white/70 text-sm">Selected: <span className="font-bold text-white">{selected.name}</span> · booking id <span className="font-mono text-white/40 text-xs">{selected.booking_id}</span></div>

          {/* Balance reconciliation */}
          <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-4 space-y-3">
            <div className="text-white/80 font-semibold text-sm flex items-center gap-1.5"><HandCoins size={14} className="text-[#F2B705]" /> Balance Reconciliation</div>
            <div className="flex items-center gap-2">
              <label className="text-white/45 text-xs">Target balance</label>
              <input data-testid="live-correction-target-balance" type="number" step="0.01" value={target} onChange={(e) => setTarget(e.target.value)}
                className="w-28 bg-[#0C0C0C] border border-white/10 rounded px-2.5 py-1.5 text-sm text-white" />
              <button data-testid="live-correction-preview-balance-btn" onClick={previewBalance} disabled={balBusy}
                className="text-xs font-semibold border border-white/15 text-white/70 px-3 py-1.5 rounded-lg hover:border-[#F2B705]/40 hover:text-[#F2B705] transition-colors disabled:opacity-50">Preview</button>
            </div>
            {balPreview && (
              <div data-testid="live-correction-balance-preview" className="text-xs space-y-1.5 border border-white/10 rounded-lg p-3">
                {balPreview.already_applied ? (
                  <div className="text-emerald-400 flex items-center gap-1.5"><CheckCircle2 size={13} /> Already correct — balance is {money(balPreview.current.balance)}. No changes needed.</div>
                ) : balPreview.can_apply ? (
                  <>
                    <div className="text-white/60">Current: total {money(balPreview.current.total_price)} · discount {money(balPreview.current.discount)} · paid {money(balPreview.current.amount_paid)} · balance {money(balPreview.current.balance)}</div>
                    <div className="text-white/85">Proposed: discount → <span className="font-bold text-[#F2B705]">{money(balPreview.proposed_discount)}</span> → new balance <span className="font-bold text-[#F2B705]">{money(balPreview.target_balance)}</span> (amount_paid unchanged)</div>
                    <div className="flex items-center gap-2 pt-1.5">
                      <input data-testid="live-correction-balance-phrase" value={balPhrase} onChange={(e) => setBalPhrase(e.target.value)}
                        placeholder='Type "APPLY CORRECTION" to confirm'
                        className="flex-1 bg-[#0C0C0C] border border-white/10 rounded px-2.5 py-1.5 text-white text-xs" />
                      <button data-testid="live-correction-apply-balance-btn" onClick={applyBalance} disabled={balBusy}
                        className="text-xs font-bold bg-[#F2B705] text-black px-3 py-1.5 rounded-lg hover:bg-white transition-colors disabled:opacity-50">Apply</button>
                    </div>
                  </>
                ) : (
                  <div className="text-red-400 flex items-center gap-1.5"><XCircle size={13} /> {balPreview.refuse_reason}</div>
                )}
              </div>
            )}
          </div>

          {/* Installment reschedule */}
          <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-white/80 font-semibold text-sm flex items-center gap-1.5"><CalendarClock size={14} className="text-[#F2B705]" /> Payment-Plan Installment Reschedule</div>
              <button data-testid="live-correction-load-candidates-btn" onClick={loadCandidates}
                className="text-xs font-semibold border border-white/15 text-white/70 px-3 py-1.5 rounded-lg hover:border-[#F2B705]/40 hover:text-[#F2B705] transition-colors">Load Installments</button>
            </div>
            {candidates && (
              candidates.length === 0 ? <div className="text-white/40 text-xs">No unpaid installments found on any payment plan for this booking.</div> : (
                <div className="space-y-1.5">
                  {candidates.map((c) => (
                    <button key={`${c.plan_id}-${c.installment_n}`} data-testid={`live-correction-installment-${c.installment_n}`}
                      onClick={() => setRescheduleSel(c)}
                      className={`w-full text-left text-xs rounded-lg border px-3 py-2 transition-colors ${rescheduleSel?.installment_n === c.installment_n ? "border-[#F2B705]/50 bg-[#F2B705]/[0.06]" : "border-white/10 hover:border-white/25"}`}>
                      Installment #{c.installment_n} · due {c.due_date} · {money(c.amount)} · {c.status}
                    </button>
                  ))}
                </div>
              )
            )}
            {rescheduleSel && (
              <div className="flex items-center gap-2">
                <label className="text-white/45 text-xs">New due date</label>
                <input data-testid="live-correction-new-due-date" type="date" value={newDue} onChange={(e) => setNewDue(e.target.value)}
                  className="bg-[#0C0C0C] border border-white/10 rounded px-2.5 py-1.5 text-sm text-white" />
                <button data-testid="live-correction-preview-reschedule-btn" onClick={previewReschedule} disabled={schedBusy || !newDue}
                  className="text-xs font-semibold border border-white/15 text-white/70 px-3 py-1.5 rounded-lg hover:border-[#F2B705]/40 hover:text-[#F2B705] transition-colors disabled:opacity-50">Preview</button>
              </div>
            )}
            {schedPreview && (
              <div data-testid="live-correction-reschedule-preview" className="text-xs space-y-1.5 border border-white/10 rounded-lg p-3">
                {schedPreview.already_applied ? (
                  <div className="text-emerald-400 flex items-center gap-1.5"><CheckCircle2 size={13} /> Already due {schedPreview.current_due_date} — no changes needed.</div>
                ) : (
                  <>
                    <div className="text-white/85">Due date: <span className="font-bold text-[#F2B705]">{schedPreview.current_due_date}</span> → <span className="font-bold text-[#F2B705]">{schedPreview.proposed_due_date}</span> ({money(schedPreview.amount)}, amount/status unchanged)</div>
                    <div className="flex items-center gap-2 pt-1.5">
                      <input data-testid="live-correction-schedule-phrase" value={schedPhrase} onChange={(e) => setSchedPhrase(e.target.value)}
                        placeholder='Type "APPLY CORRECTION" to confirm'
                        className="flex-1 bg-[#0C0C0C] border border-white/10 rounded px-2.5 py-1.5 text-white text-xs" />
                      <button data-testid="live-correction-apply-reschedule-btn" onClick={applyReschedule} disabled={schedBusy}
                        className="text-xs font-bold bg-[#F2B705] text-black px-3 py-1.5 rounded-lg hover:bg-white transition-colors disabled:opacity-50">Apply</button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
